import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyProfile, TranslationLanguages } from '@up2d8/contracts';
import {
  Avatar,
  Button,
  Card,
  CardTitle,
  FileUpload,
  LoadingState,
  Select,
  useToast,
} from '@up2d8/ui';
import { DeskShell } from './desk/desk-shell';
import { useSession } from '../../shared/session';
import { TenantSwitcher } from '../../shared/tenant-switcher';
import { useHasTeam } from './use-my-team';
import { useFileUrl, useUpload } from '../../shared/use-upload';
import { LanguageSwitcher } from '../../shared/language-switcher';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';
import { isNativeApp } from '../../shared/native';
import { MfaSelfCard } from '../identity/mfa-self-card';

/**
 * De medewerker kiest zijn eigen profielfoto.
 *
 * Dit is voor veel mensen het eerste wat ze zelf in het platform doen, en vaak op een telefoon
 * met één hand, in werkkleding, op een luidruchtige vloer. Daarom: grote knoppen, een
 * cameraknop die de camera opent, en één ding per scherm.
 *
 * Er staat geen verplichting achter. Een foto maakt een naam herkenbaar in een lijst; wie hem
 * niet wil, houdt zijn initialen en mist niets.
 */
export function AppProfilePage() {
  const { t } = useTranslation();
  const { api, profile: session, has, logout } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const tabs = useAppTabs();
  const queryClient = useQueryClient();
  const upload = useUpload();
  const fileUrl = useFileUrl();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const profile = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/me/profile');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as MyProfile;
    },
  });

  // De URL is zestig seconden geldig, dus hij wordt bij elk bezoek opnieuw gevraagd en niet
  // met het profiel meegecached.
  useEffect(() => {
    const fileId = profile.data?.photoFileId;
    if (!fileId) {
      setPhotoUrl(null);
      return;
    }
    let current = true;
    void fileUrl(fileId, 'thumbnail').then((url) => {
      if (current) setPhotoUrl(url);
    });
    return () => {
      current = false;
    };
  }, [profile.data?.photoFileId, fileUrl]);

  const setPhoto = useMutation({
    mutationFn: async (fileId: string | null) => {
      const { error } = await api.PUT('/api/v1/me/profile/photo', { body: { fileId } });
      if (error) throw error;
    },
    onSuccess: async (_result, fileId) => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.show({
        title: fileId ? t('profilePhoto.saved') : t('profilePhoto.removed'),
        tone: 'success',
      });
    },
    onError: () => toast.show({ title: t('errors.generic'), tone: 'danger' }),
  });

  /**
   * De talen waarin dit bedrijf berichten kan laten lezen.
   *
   * `enabled` bepaalt of deze kaart er staat. Een taalkeuze die niets doet — omdat de klant
   * vertalen uit heeft — is erger dan geen taalkeuze: de medewerker kiest Roemeens, krijgt
   * Nederlands, en concludeert dat het platform niet werkt.
   */
  const languages = useQuery({
    queryKey: ['translation', 'languages'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/translation/languages', {});
      return (data as TranslationLanguages | undefined) ?? { enabled: false, languages: [] };
    },
  });

  const setReadingLanguage = useMutation({
    mutationFn: async (code: string) => {
      const { error } = await api.PATCH('/api/v1/me/profile', {
        body: { readingLanguage: code === '' ? null : code } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      // De feed en het geopende bericht staan nog in de oude taal in de cache.
      await queryClient.invalidateQueries({ queryKey: ['feed'] });
      toast.show({ title: t('readingLanguage.saved'), tone: 'success' });
    },
    onError: () => toast.show({ title: t('errors.generic'), tone: 'danger' }),
  });

  const onSelect = async (file: File): Promise<void> => {
    const fileId = await upload.upload(file, {
      ownerModule: 'directory',
      purpose: 'profile_photo',
    });
    if (fileId) await setPhoto.mutateAsync(fileId);
  };

  const onLogout = async () => {
    await logout();
    void navigate({ to: '/app/login' });
  };

  const name = profile.data?.displayName ?? session?.displayName ?? '';

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="profile"
      renderLink={renderAppTabLink}
    >
      <h1 className="font-heading text-page-title font-semibold text-text-primary">
        {t('profilePhoto.title')}
      </h1>

      {profile.isPending ? (
        <LoadingState rows={3} label={t('common.loading')} />
      ) : (
        <Card>
          <CardTitle>{t('profilePhoto.current')}</CardTitle>

          <FileUpload
            className="mt-element"
            label={t('profilePhoto.choose')}
            accept="image/*"
            hint={t('profilePhoto.hint')}
            error={upload.error}
            progress={upload.progress}
            fileName={upload.fileName}
            disabled={setPhoto.isPending}
            onSelect={(file) => void onSelect(file)}
            onCancel={upload.cancel}
            labels={{
              choose: t('media.choose'),
              camera: t('media.camera'),
              cancel: t('common.cancel'),
            }}
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={t('profilePhoto.altText', { name })}
                className="h-24 w-24 rounded-pill object-cover"
              />
            ) : (
              <Avatar name={name} self size="xl" />
            )}
          </FileUpload>

          {profile.data?.photoFileId && (
            <Button
              variant="secondary"
              className="mt-element"
              loading={setPhoto.isPending}
              onClick={() => void setPhoto.mutateAsync(null)}
            >
              {t('profilePhoto.remove')}
            </Button>
          )}
        </Card>
      )}

      {/*
          De leestaal. Eén keuze, en ze gaat over berichten — niet over de knoppen in deze app: die
          bestaan in drie talen (ADR-0043), en dat is een andere belofte dan "wij vertalen alles".
          De namen staan in de taal zelf, want wie deze lijst nodig heeft, leest dit scherm niet vlot.
        */}
      {languages.data?.enabled && (
        <Card>
          <CardTitle>{t('readingLanguage.title')}</CardTitle>
          <p className="mt-1 text-content text-text-secondary">{t('readingLanguage.hint')}</p>

          <Select
            className="mt-element"
            label={t('readingLanguage.label')}
            value={profile.data?.readingLanguage ?? ''}
            disabled={setReadingLanguage.isPending}
            onChange={(event) => void setReadingLanguage.mutateAsync(event.target.value)}
          >
            <option value="">{t('readingLanguage.sourceLanguage')}</option>
            {languages.data.languages.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name}
              </option>
            ))}
          </Select>
        </Card>
      )}

      {/* Tweestapsverificatie (FBK-12): aanzetten, herstelcodes, uitzetten. */}
      <Card>
        <MfaSelfCard />
      </Card>

      <Card compact>
        <CardTitle>{t('appHome.settings')}</CardTitle>
        <div className="mt-element flex flex-wrap gap-inline">
          {/* De collega-lijst staat achter het recht: een bedrijf dat de lijst uitzet, hoort hier
              geen knop te zien die op een 404 uitkomt. */}
          {has('directory.colleagues.read') && (
            <Link to="/app/collegas">
              <Button variant="secondary">{t('colleagues.link')}</Button>
            </Link>
          )}
          {/*
            Mijn team staat in het profielmenu en niet in de onderbalk (Stap 17, spec §1): alleen
            leidinggevenden hebben dit scherm, en een zesde tab maakt elk raakvlak te smal. Wie
            geen team heeft, ziet de knop niet - die beslissing zit in `useHasTeam`.
          */}
          <TeamMenuLink />
          {/* De plank met opgeslagen berichten (SCH-1). Hier en niet als filter op de tijdlijn:
              je gaat er bewust naartoe, en de tijdlijn gaat over wat er nieuw is. */}
          <Link to="/app/opgeslagen">
            <Button variant="secondary">{t('interaction.savedPosts')}</Button>
          </Link>
          <Link to="/app/settings/pin">
            <Button variant="secondary">{t('pin.change')}</Button>
          </Link>
          <Link to="/app/settings">
            <Button variant="secondary">{t('push.manage')}</Button>
          </Link>
          {/* Wie de native app gebruikt, heeft ze al geïnstalleerd (MOB-0). */}
          {isNativeApp() ? null : (
            <Link to="/app/install">
              <Button variant="secondary">{t('install.link')}</Button>
            </Link>
          )}
          <Link to="/app/privacy/werkgever">
            <Button variant="secondary">{t('privacy.link')}</Button>
          </Link>
        </div>

        {/*
          Afmelden staat apart, onder een scheidslijn.

          Het stond tot UI-2 op het **beginscherm**, tussen de berichten en de snelkoppelingen in.
          Dat is twee keer verkeerd: het scherm waar je het vaakst bent, hoort niet de knop te
          dragen die je eruit gooit, en instellingen horen bij "Ik". Hier staat het bij de rest van
          wat over jou gaat, en met een lijn ertussen, want het is het enige in deze kaart dat iets
          beëindigt in plaats van iets opent.
        */}
        {/* Wisselen tussen bedrijven (ACC-1), alleen wie er meer dan één heeft. */}
        <div className="mt-element">
          <TenantSwitcher zone="app" />
        </div>

        <div className="mt-element border-t border-border-light pt-element">
          <Button variant="secondary" onClick={() => void onLogout()}>
            {t('common.logout')}
          </Button>
        </div>
      </Card>

      <div className="flex flex-col items-center gap-2 pt-element">
        <LanguageSwitcher />
        {/*
          De enige plek waar UP2D8 nog staat in de medewerkerszone (ADR-0071).

          De schil draagt het merk van de werkgever; wie wil weten waar deze app vandaan komt,
          vindt het hier. Niet in een voettekst onder elke pagina: dan zou het merk langs de
          achterdeur weer in elk scherm staan.
        */}
        <p className="text-caption text-text-muted" data-testid="via-up2d8">
          {t('common.viaUp2d8')}
        </p>
      </div>
    </DeskShell>
  );
}

/** De knop "Mijn team", alleen voor wie er een heeft (Stap 17, TEAM-2). */
function TeamMenuLink() {
  const { t } = useTranslation();
  const { hasTeam } = useHasTeam();
  if (!hasTeam) return null;
  return (
    <Link to="/app/team">
      <Button variant="secondary" data-testid="team-menu-link">
        {t('team.link')}
      </Button>
    </Link>
  );
}
